// src/components/workflow/CustomNode.jsx
import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  UserPlus,
  GripHorizontal,
} from "lucide-react";
import { handleCustomResponse } from "../../utils/response";

export const WorkflowNameNode = memo(({ data }) => {
  return (
    <div className="workflow-name-node shadow-4">
      <p className="fs-5">{data.label}</p>
      <Handle
        type="source"
        position={Position.Bottom}
        className="custom-handle"
      />
    </div>
  );
});
export const CustomStep = memo(({ id, data }) => {
  const handleDragStart = (event, fieldId) => {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ fieldId, nodeId: id })
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const rawData = event.dataTransfer.getData("application/json");

    // Check if data exists and is not empty
    if (!rawData || rawData.trim() === "") {
      return;
    }

    let draggedData;
    try {
      draggedData = JSON.parse(rawData);
    } catch (error) {
      handleCustomResponse("error", "Invalid drag-and-drop data");
      return;
    }
    const { fieldId: draggedFieldId, nodeId: sourceNodeId } = draggedData;

    // Cross-step drag & drop
    if (sourceNodeId !== id) {
      // Field ko source step se remove karo aur current step mein add karo
      data.onMoveFieldToOtherStep?.(draggedFieldId, sourceNodeId, id);
      return;
    }

    // Same step ke andar reordering - sirf tab karo jab fields hain
    if (data.fields?.length > 0) {
      const targetFieldElement = event.target.closest(".field-item");
      if (!targetFieldElement) return;

      const targetFieldId = targetFieldElement.dataset.fieldId;
      if (!draggedFieldId || !targetFieldId) return;

      const updatedFields = [...data.fields];
      const draggedIndex = updatedFields.findIndex(
        (f) => f.id === draggedFieldId
      );
      const targetIndex = updatedFields.findIndex((f) => f.id === targetFieldId);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const [draggedField] = updatedFields.splice(draggedIndex, 1);
      updatedFields.splice(targetIndex, 0, draggedField);

      const reorderedFields = updatedFields.map((f, index) => ({
        ...f,
        order: index + 1,
      }));

      data.onUpdateFields(id, reorderedFields);
    }
  };

  const handleMoveField = (fieldId, direction) => {
    const updatedFields = [...data.fields];
    const index = updatedFields.findIndex((f) => f.id === fieldId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === updatedFields.length - 1)
    ) {
      return;
    }

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    const reorderedFields = updatedFields.map((f, i) => ({
      ...f,
      order: i + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  return (
    <div
      className="custom-node-wrapper shadow-4"
      style={{
        "--node-border-color-hover": "#cbd5e1",
        "--node-border-color": "#e5e7eb",
      }}
    >
      <div
        className="custom-node-header"
        style={{
          background:
            "linear-gradient(135deg, #862509 0%, #f85930 50%, #f99e5f 100%)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-white">{data.label}</div>
          <div className="d-flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                data.onEditStep(id);
              }}
              className="custom-node-action-btn"
              title="Edit Step"
            >
              <Edit size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                data.onRemoveNode(id);
              }}
              className="custom-node-action-btn danger"
              title="Delete Step"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="content-container">
        <div className="scroll-shadow-top" />
        <div className="scroll-shadow-bottom" />
        {data.fields?.length > 0 ? (
          <div
            className="custom-scrollbar field-list"
            onScroll={(e) => {
              const container = e.target.closest(".content-container");
              if (container) {
                container.dataset.scrollTop = e.target.scrollTop > 10;
                container.dataset.scrollBottom =
                  e.target.scrollTop <
                  e.target.scrollHeight - e.target.clientHeight - 10;
              }
            }}
          >
            {data.fields
              .sort((a, b) => a.order - b.order)
              .map((field, index) => (
                <div
                  key={field.id}
                  data-field-id={field.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.id)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="field-item"
                >
                  <div className="drag-handle">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, index >= 0 ? "down" : "up");
                      }}
                      disabled
                      style={{ cursor: "pointer" }}
                      title="Drag"
                    >
                      <GripHorizontal size={24} />
                    </button>
                  </div>
                  <span className="field-label">
                    <div className="field-dot" />
                    <span className="truncate">
                      {field.label || field.name}
                    </span>
                    {field.required && (
                      <span className="required-asterisk">*</span>
                    )}
                  </span>
                  <div className="field-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "up");
                      }}
                      disabled={index === 0}
                      className="field-action-btn"
                      title="Move Up"
                    >
                      <ChevronUp size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "down");
                      }}
                      disabled={index === data.fields.length - 1}
                      className="field-action-btn"
                      title="Move Down"
                    >
                      <ChevronDown size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onEditField(field, id);
                      }}
                      className="field-action-btn"
                      title="Edit Field"
                    >
                      <Edit size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onDeleteField(field.id, id);
                      }}
                      className="field-action-btn"
                      title="Delete Field"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div 
            className="no-fields-placeholder"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ cursor: 'pointer', minHeight: '100px' }}
          >
            <div className="no-fields-icon-bg">
              <div className="no-fields-icon-inner" />
            </div>
            <div className="fw-bold text-secondary-light text-xs">
              No fields yet
            </div>
            <div className="text-xs text-neutral-400">
              Drag components or fields from other steps here to start.
            </div>
          </div>
        )}
      </div>
      <Handle type="target" position={Position.Top} className="custom-handle" />
      <Handle
        type="source"
        position={Position.Bottom}
        className="custom-handle"
      />
    </div>
  );
});